// src/encounter/lib/rail-note-piece.ts
//
// The left rail's "Add to note" / "Add a note" (left-rail target 4 Nov §4, §4b round 4, §4d, §7.2). Pure functions, no React, no look.
//
//  - STARTED visit, "Add to note" on a problem, an allergy or a medication: ONE attributable chart piece goes into a LINE of the
//    CURRENT encounter note by the existing chart-piece mechanism (`note-pieces.ts`; in a note line the piece is a run carrying the
//    `cps-piece` attribute, kept in the line's `cps-note-delta` beside its plain text: `note-line-delta.ts`, the same run a "/" chart
//    value or a "." phrase writes). It names the record it came from (`sourceId`, this patient, this visit) and freezes with the note
//    (a piece is a snapshot: R6). §7.2 landings: HPI = the HPI section's narrative line (the `hpi-narrative` phrase editor), ROS = the
//    ROS section's narrative line (`ros-narrative`). Assessment text has NO landing yet (§4d: the Assessment free-text box does not
//    exist; the A&P narrative is never used as a stand-in), so its state is always "absent". Diagnoses are not a note piece: they are
//    the page's `handleAddDx` (rail-note-targets.ts).
//  - PRE-VISIT, "Add a note" on a problem: a comment on the problem record that APPENDS to the existing note text with the author
//    and the D-45 date; it never overwrites (today's "+ Note" replaced the single note field: the defect).
import { clinicDateD45 } from '@/shared/lib/clinic-time';
import { PIECE_EMBED, newPieceId, wallClockNow, type ChartPiece, type ChartQuery } from './note-pieces';
import { appendPhrase, lineSegs, plainSegs, segsText, segsToDelta, type LineSeg } from './note-line-delta';

/** A note line a rail piece can land in (§7.2). */
export type RailNoteLanding = 'hpi' | 'ros' | 'assessment';

/** Whether a landing can take a piece now. `absent`: the note has no such line for this user (the target is not offered at all,
 *  Elation's "if available"); `blocked`: the line exists but cannot take it now, and `reason` says why (the disabled reason). */
export type RailLandingState = { kind: 'ready' } | { kind: 'absent' } | { kind: 'blocked'; reason: string };

export const LANDING_READY: RailLandingState = { kind: 'ready' };
export const LANDING_ABSENT: RailLandingState = { kind: 'absent' };
export const landingBlocked = (reason: string): RailLandingState => ({ kind: 'blocked', reason });

/** What the page hands the rail so "Add to note" can write into the current encounter note. */
export interface RailNotePieceHost {
  /** The landing's state now; given `sourceId`, a line that already holds a piece quoting that record is `blocked`
   *  (`ALREADY_IN_NOTE`: a duplicate add is a no-op). */
  state: (landing: RailNoteLanding, sourceId?: string) => RailLandingState;
  /** Insert ONE piece (and the clinician's typed text, if any) at the end of `landing`'s line. Called only when ready. */
  insert: (landing: RailNoteLanding, piece: ChartPiece, typedText?: string) => void;
}

/** The reason when the page offers no encounter note at all (pre-visit, legacy hosts): UC-30 rule 8's wording (left-rail target §4b). */
export const NO_NOTE_REASON = 'Start or open a visit to add to its note';

/** The same record twice into the same note section is a no-op (left-rail target §4b, Scrips rule): this is the reason it says. */
export const ALREADY_IN_NOTE = 'Already in note';

const NOT_RECORDED = 'not recorded';
const clean = (v: string | null | undefined) => (v ?? '').trim();

/** The problem line (§4). A physician's Assessment line: "<curated name> (<ICD-10 code>)". For a nurse or allied health (ruling 6):
 *  "Problem list entry: <name> (<code>), status <clinical status>, verification <status or 'not recorded'>". */
export function problemNoteLine(
  p: { name: string; code?: string | null; clinicalStatus?: string | null; verificationStatus?: string | null },
  persona: 'physician' | 'other',
): string {
  const name = clean(p.name) || 'Problem';
  const code = clean(p.code) || `code ${NOT_RECORDED}`;
  if (persona === 'physician') return `${name} (${code})`;
  const status = clean(p.clinicalStatus) || NOT_RECORDED;
  const verification = clean(p.verificationStatus) || NOT_RECORDED;
  return `Problem list entry: ${name} (${code}), status ${status}, verification ${verification}`;
}

/** The medication line (§4): "<display name> <dose/route/schedule as recorded>", a missing part reading "not recorded". */
export function medicationNoteLine(m: { name: string; dose?: string | null }): string {
  const name = clean(m.name) || 'Medication';
  const dose = clean(m.dose);
  return `${name} ${dose || `dose, route and schedule ${NOT_RECORDED}`}`;
}

/** The allergy line (§4b round 4 rules an allergy's targets, not its words): the allergy as the rail row shows it, its name and,
 *  when the row carries one, its detail as recorded in brackets. Nothing is added that the record does not hold. */
export function allergyNoteLine(a: { name: string; detail?: string | null }): string {
  const name = clean(a.name) || 'Allergy';
  const detail = clean(a.detail);
  return detail ? `${name} (${detail})` : name;
}

/** ONE chart piece quoting one record, resolved now: `display` is the line, `sourceId` the record, the pull names this patient and
 *  this visit (so the note's provenance check, `chartOrigins`, reads it as "here"). */
export function railNotePiece(args: {
  query: Extract<ChartQuery, 'problems.active' | 'medications.active' | 'allergies.list'>;
  display: string;
  sourceId: string;
  patientId?: string;
  encounterId?: string;
  now?: Date;
  id?: string;
}): ChartPiece {
  return {
    v: 1,
    t: 'chart',
    id: args.id ?? newPieceId(),
    query: args.query,
    snapshot: {
      query: args.query,
      ...(args.patientId ? { patientId: args.patientId } : {}),
      ...(args.encounterId ? { encounterId: args.encounterId } : {}),
      pulledAt: wallClockNow(args.now),
      status: 'ok',
      results: [{ sourceId: args.sourceId, display: args.display }],
    },
  };
}

/** The line holds a piece quoting `sourceId` (a rail "Add to note" of that record): only a Delta that still derives the stored text
 *  counts (`lineSegs`; a stale Delta is plain text, so its pieces are gone). */
export function lineHasPieceFrom(delta: string | undefined, value: unknown, sourceId: string): boolean {
  const segs = lineSegs(delta, value);
  return !!segs?.some((s) => s.piece?.t === 'chart' && !!s.piece.snapshot?.results.some((r) => r.sourceId === sourceId));
}

/**
 * The line (plain text + its `cps-note-delta`) with ONE piece appended on its own line, and the typed text after it
 * ("<piece> — <typed>"). APPEND ONLY: every existing run is kept, pieces included (a stale Delta reads as the stored text). The
 * separator is the line's own phrase rule (`appendPhrase`: a new line unless the text already ends in white space).
 */
export function appendPieceToLine(value: string, delta: string | undefined, piece: ChartPiece, typedText?: string): { value: string; delta: string } {
  const segs: LineSeg[] = lineSegs(delta, value) ?? plainSegs(value);
  const typed = clean(typedText);
  const next = appendPhrase(segs, [{ insert: { [PIECE_EMBED]: piece } }, ...(typed ? [{ insert: ` — ${typed}` }] : [])]);
  return { value: segsText(next), delta: segsToDelta(next) };
}

/**
 * Pre-visit "Add a note" on a problem (§4): the existing note with ONE new line appended, "<text> — <name>, <D-45 date>". The
 * earlier text is kept exactly; an empty `text` changes nothing.
 */
export function appendAttributedNote(existing: string | null | undefined, text: string, author: string | null | undefined, atIso: string): string {
  const body = clean(text);
  const prev = existing ?? '';
  if (!body) return prev;
  const who = clean(author) || 'Unknown author';
  const when = clinicDateD45(atIso) ?? atIso;
  const line = `${body} — ${who}, ${when}`;
  if (!prev.trim()) return line;
  return `${prev.replace(/\s+$/, '')}\n${line}`;
}
